import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    // Set cookie with same attributes used for auth tokens
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    // MUST clear with exact same attributes
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure; Partitioned`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const [name, ...rest] = c.trim().split('=');
          return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage()).find((c) =>
    c.name.includes('auth-token')
  )?.value ?? null;



export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet: { name: string; value: string; options?: any }[]) {
          if (typeof document === 'undefined') return;
          const kEnabled = canUseCookies();
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              if (kEnabled) {
                setCookie(name, value, options);
              } else {
                try {
                  localStorage.setItem(`${PFX}${name}`, value);
                } catch {
                  /* ignore */
                }
              }
            } else {
              // Clear cookie
              if (kEnabled) {
                document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure; Partitioned`;
                if (options?.domain) {
                  document.cookie = `${name}=; Path=/; Max-Age=0; Domain=${options.domain}; SameSite=None; Secure; Partitioned`;
                }
              }
              // Clear localStorage
              try {
                localStorage.removeItem(`${PFX}${name}`);
              } catch {
                /* ignore */
              }
            }
          });
        },
      },
    }
  );
}
